import { zodResolver } from "@hookform/resolvers/zod";
import { composeProgress } from "apollo/utils";
import {
  DOCS_FORM_NAMES,
  EXTRACT_OCR_STATUS,
  PATIENT_DOCUMENTS,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
  REHAB_FORM_TO_PATIENT_DOCUMENT,
} from "core/consts";
import { useExtractDocs } from "core/model/api/endpoints/extractDocs/context";
import { useDocsFillForm } from "core/model/api/endpoints/extractDocs/hooks/docs/form";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import {
  Auction,
  AuctionForm,
  DocsFormDataMapped,
  DocsFormName,
  DocsFormStatusResponse,
  FormMetadata,
  Patient,
  QueryProgress,
  RehabForm,
  RehabFormName,
  RehabForms,
  SourceFields,
  TransitionalCare,
  TransitionalCareFormData,
} from "core/types";
import {
  SAVE_AND_CREATE_PDF_ID,
  SAVE_AND_OVERWRITE_PDF_ID,
} from "dsl/ecosystems/PatientForms/RehabForms/RehabFormWrapper";
import {
  CreateRehaPdfProps,
  useCreateRehaPdf,
} from "dsl/ecosystems/PatientForms/RehabForms/useCreateRehaPdf";
import {
  getRehabFormInputValue,
  RehabFormDataMapped,
  useGetRehabFormSubmit,
} from "dsl/ecosystems/PatientForms/RehabForms/utils";
import {
  getTransitionalCareFormData,
  useGetTransitionalCareOnSubmit,
} from "dsl/ecosystems/PatientForms/TransitionalCare/Form";
import {
  CreateTransitionalCarePdfProps,
  useCreateTransitionalCarePdf,
} from "dsl/ecosystems/PatientForms/useCreateTransitionalCarePdf";
import { useDocumentHandling } from "dsl/ecosystems/PatientForms/useDocumentHandling";
import { useImportFormData } from "dsl/ecosystems/PatientForms/utils";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Path, PathValue, useForm } from "react-hook-form";
import { useTranslations } from "translations";
import { z } from "zod";
import { DOCS_FORM_FOOTER_SCREENS, DocsFormFooterScreen } from ".";
import { FOOTER_BANNERS, FooterBannerType } from "./Footer";
import { useGetFormFieldConfig } from "./Forms";
import { useGetDischargeLetterOnSubmit } from "./Forms/DischargeLetters/onSubmitForm";
import { getDischargeLetterForm } from "./Forms/DischargeLetters/useGetForm";
import { HEADER_BANNERS, HeaderBannerAction } from "./HeaderBanners";
import { useDeleteConfirmation } from "./onDelete";
import { MigratedForms } from "./utils";

// to be deprecated in favour of joint docs fetch
function useGetFormData<T extends DocsFormName>({
  dischargeLetterForms,
  formName,
  rehabForms,
  transitionalCare,
}: {
  formName: T;
  dischargeLetterForms: AuctionForm[];
  rehabForms: RehabForms | undefined;
  transitionalCare: TransitionalCare | AuctionForm | undefined;
}): {
  form: RehabForm | null | undefined;
  formData: DocsFormDataMapped<T> | null | undefined;
  metadata?: FormMetadata<T> | undefined;
} {
  return useMemo(() => {
    switch (formName) {
      case DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT:
      case DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY:
        return getDischargeLetterForm({
          formName,
          forms: dischargeLetterForms,
        });
      case DOCS_FORM_NAMES.TRANSITIONAL_CARE:
        return {
          form: transitionalCare,
          formData: getTransitionalCareFormData({
            transitionalCare,
          }),
        };
      default:
        return getRehabFormInputValue({ formName, forms: rehabForms });
    }
  }, [formName, rehabForms, transitionalCare, dischargeLetterForms]) as {
    form: RehabForm | null | undefined;
    formData: DocsFormDataMapped<T> | null | undefined;
    metadata?: FormMetadata<T> | undefined;
  };
}

// to be deprecated in favour of joint docs submit
function useGetOnSubmit<T extends DocsFormName>({
  auction,
  docsFillFormResult,
  formData,
  formName,
  hasPreviousFormValue,
  rehabForms,
  sourceInformation,
}: {
  docsFillFormResult: DocsFormStatusResponse | null;
  formName: T;
  auction: Auction;
  rehabForms: RehabForms | undefined;
  hasPreviousFormValue: boolean;
  sourceInformation: SourceInformation<T>;
  metadata: FormMetadata<T> | undefined;
  formData: DocsFormDataMapped<T> | null | undefined;
}) {
  const {
    onSubmit: onSubmitRehabForm,
    onSubmitProgress: onSubmitRehabFormProgress,
  } = useGetRehabFormSubmit({
    auction,
    formName: formName as RehabFormName,
    forms: rehabForms,
    isMedicalFormApproved: rehabForms?.medical_form?.is_doctor_approved,
  });
  const {
    onSubmit: onSubmitTransitionalCare,
    onSubmitProgress: onSubmitTransitionalCareProgress,
  } = useGetTransitionalCareOnSubmit({
    auction,
  });
  const {
    onSubmit: onSubmitDischargeLetter,
    onSubmitProgress: onSubmitDischargeLetterProgress,
  } = useGetDischargeLetterOnSubmit({
    auction,
    formData,
  });

  const onSubmit = (formDataState: DocsFormDataMapped<T>, isDirty: boolean) => {
    switch (formName) {
      case DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT:
      case DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY:
        return onSubmitDischargeLetter(
          formDataState as DocsFormDataMapped<MigratedForms>,
          formName,
          sourceInformation as SourceInformation<MigratedForms>,
          docsFillFormResult,
        );
      case DOCS_FORM_NAMES.TRANSITIONAL_CARE:
        return onSubmitTransitionalCare(
          formDataState as TransitionalCareFormData,
          {
            dirty: isDirty,
          },
        );
      default:
        return onSubmitRehabForm(
          formDataState as RehabFormDataMapped<RehabFormName>,
          { dirty: isDirty },
          {} as React.MouseEvent<HTMLButtonElement>,
          hasPreviousFormValue
            ? SAVE_AND_OVERWRITE_PDF_ID
            : SAVE_AND_CREATE_PDF_ID,
        );
    }
  };

  return {
    onSubmit,
    onSubmitProgress: composeProgress([
      onSubmitRehabFormProgress,
      onSubmitTransitionalCareProgress,
      onSubmitDischargeLetterProgress,
    ]),
  };
}

function useCreatePdf<T extends DocsFormName>({
  auction,
  footerScreen,
  formData,
  formName,
  isDirty,
  patient,
  rehabForms,
  setFooterBanner,
  setFooterScreen,
  transitionalCare,
}: {
  isDirty: boolean;
  formName: T;
  auction: Auction;
  patient: Patient;
  footerScreen: DocsFormFooterScreen;
  setFooterScreen: Dispatch<SetStateAction<DocsFormFooterScreen>>;
  rehabForms: RehabForms | undefined;
  transitionalCare: TransitionalCare | AuctionForm | undefined;
  formData: DocsFormDataMapped<T> | undefined;
  setFooterBanner: (banner: FooterBannerType | null) => void;
}) {
  const createRehaPdf = useCreateRehaPdf();
  const createTransitionalCarePdf = useCreateTransitionalCarePdf();

  const externalId = auction.patient.external_id ?? "";

  const transitionalCareHandlers =
    useDocumentHandling<CreateTransitionalCarePdfProps>({
      careseekerName: patient.careseeker?.name ?? "",
      createPDFDocument: createTransitionalCarePdf,
      externalId,
      userId: auction.patient.user_id ?? "",
    });

  const rehaHandlers = useDocumentHandling<CreateRehaPdfProps>({
    careseekerName: patient.careseeker?.name ?? "",
    createPDFDocument: createRehaPdf,
    externalId,
    userId: auction.patient.user_id ?? "",
  });

  const { progressOpenPdf, progressSendToHis } =
    formName === DOCS_FORM_NAMES.TRANSITIONAL_CARE
      ? transitionalCareHandlers
      : rehaHandlers;

  const handleWarnOpenPdf = async () => {
    if (isDirty && footerScreen !== DOCS_FORM_FOOTER_SCREENS.WARN_OPEN_PDF) {
      setFooterScreen(DOCS_FORM_FOOTER_SCREENS.WARN_OPEN_PDF);
      return;
    }
    if (formName === DOCS_FORM_NAMES.TRANSITIONAL_CARE && transitionalCare) {
      await transitionalCareHandlers.handleOpenPdf({
        transitionalCare,
      });
    } else {
      await rehaHandlers.handleOpenPdf({
        documentNumber: -1,
        externalId,
        formData: formData as RehabFormDataMapped<RehabFormName>,
        formName: formName as RehabFormName,
        forms: rehabForms,
      });
    }
    if (footerScreen === DOCS_FORM_FOOTER_SCREENS.WARN_OPEN_PDF) {
      setFooterScreen(DOCS_FORM_FOOTER_SCREENS.DEFAULT);
    }
  };

  const handleWarnSendToHis = async () => {
    if (isDirty && footerScreen !== DOCS_FORM_FOOTER_SCREENS.WARN_SEND_TO_HIS) {
      setFooterScreen(DOCS_FORM_FOOTER_SCREENS.WARN_SEND_TO_HIS);
      return;
    }

    if (formName === DOCS_FORM_NAMES.TRANSITIONAL_CARE && transitionalCare) {
      await transitionalCareHandlers.handleSendToHis({
        documentType: PATIENT_DOCUMENTS.TRANSITIONAL_CARE,
        transitionalCare,
      });
    } else {
      await rehaHandlers.handleSendToHis({
        documentNumber: -1,
        documentType: REHAB_FORM_TO_PATIENT_DOCUMENT[formName as RehabFormName],
        externalId,
        formData: formData as RehabFormDataMapped<RehabFormName>,
        formName: formName as RehabFormName,
        forms: rehabForms,
      });
    }
    if (footerScreen === DOCS_FORM_FOOTER_SCREENS.WARN_SEND_TO_HIS) {
      setFooterScreen(DOCS_FORM_FOOTER_SCREENS.DEFAULT);
    }
    setFooterBanner(FOOTER_BANNERS.SEND_TO_HIS_SUCCESS);
  };

  return {
    handleOpenPdf: handleWarnOpenPdf,
    handleSendToHis: handleWarnSendToHis,
    progressOpenPdf,
    progressSendToHis,
  };
}

export type SourceSelected<T extends DocsFormName> = {
  fieldName: keyof DocsFormDataMapped<T>;
  sourceFields: SourceFields;
};

const useSelectedSource = <T extends DocsFormName>({
  docsFillFormResult,
  headerBannerAction,
  savedMetadata,
}: {
  docsFillFormResult: DocsFormStatusResponse | null;
  savedMetadata: FormMetadata<T> | undefined;
  headerBannerAction: HeaderBannerAction;
}) => {
  const [selectedSource, setSelectedSource] = useState<
    SourceSelected<T> | undefined
  >(undefined);

  const sourceFields = useMemo(() => {
    return Object.entries(docsFillFormResult?.form ?? {}).reduce(
      (acc, [key, inferredValue]) => {
        const savedFieldMetadata =
          savedMetadata?.source_fields[key as keyof DocsFormDataMapped<T>];
        const inferredSourceDocuments = inferredValue.source_documents;
        return {
          ...acc,
          [key]: {
            ...(savedFieldMetadata ?? {}),
            manually_edited:
              inferredValue.value != null &&
              headerBannerAction.banner !== HEADER_BANNERS.IMPORT_WARNING
                ? false
                : !!savedFieldMetadata?.manually_edited,
            source_documents: inferredSourceDocuments
              ? inferredSourceDocuments
              : savedFieldMetadata?.source_documents ?? [],
          },
        };
      },
      (savedMetadata?.source_fields ?? {}) as FormMetadata<T>["source_fields"],
    );
  }, [docsFillFormResult?.form, savedMetadata, headerBannerAction]);

  const handleSetSelectedSource = useCallback(
    (fieldName: keyof DocsFormDataMapped<T>) => {
      setSelectedSource({
        fieldName,
        sourceFields: sourceFields[fieldName] ?? {},
      });
    },
    [sourceFields],
  );

  const isSourceSelected = useCallback(
    (fieldName: keyof DocsFormDataMapped<T>) => {
      return selectedSource?.fieldName === fieldName;
    },
    [selectedSource],
  );

  const getSourceFields = useCallback(
    (fieldName: keyof DocsFormDataMapped<T>) => {
      return sourceFields[fieldName] ?? {};
    },
    [sourceFields],
  );

  const resetSelectedSource = useCallback(() => {
    setSelectedSource(undefined);
  }, []);

  return {
    getSourceFields,
    handleSetSelectedSource,
    isSourceSelected,
    resetSelectedSource,
    selectedSource,
    sourceFields,
  } as const;
};

export type SourceInformation<T extends DocsFormName> = ReturnType<
  typeof useSelectedSource<T>
>;

const useFooterBanner = () => {
  const [footerBanner, setFooterBanner] = useState<FooterBannerType | null>(
    null,
  );

  const footerBannerTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSetFooterBanner = useCallback(
    (banner: FooterBannerType | null) => {
      if (footerBannerTimeoutRef.current) {
        clearTimeout(footerBannerTimeoutRef.current);
      }

      setFooterBanner(banner);

      if (banner) {
        footerBannerTimeoutRef.current = setTimeout(() => {
          setFooterBanner(null);
        }, 5000);
      }
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (footerBannerTimeoutRef.current) {
        clearTimeout(footerBannerTimeoutRef.current);
      }
    };
  }, []);

  return { footerBanner, setFooterBanner: handleSetFooterBanner };
};

export function useDocsForm<T extends DocsFormName>({
  auction,
  dischargeLetterForms,
  formName,
  patient,
  rehabForms,
  transitionalCare,
}: {
  auction: Auction;
  dischargeLetterForms: AuctionForm[];
  formName: T;
  rehabForms: RehabForms | undefined;
  transitionalCare: TransitionalCare | AuctionForm | undefined;
  patient: Patient;
}) {
  const getOntology = useGetOntology();
  const translations = useTranslations();
  const [headerBannerAction, setHeaderBannerAction] =
    useState<HeaderBannerAction>({
      banner: null,
    });

  const [footerScreen, setFooterScreen] = useState<DocsFormFooterScreen>(
    DOCS_FORM_FOOTER_SCREENS.DEFAULT,
  );
  const { footerBanner, setFooterBanner } = useFooterBanner();

  const [importFormProgress, setImportFormProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const { extractOcrStatus } = useExtractDocs();
  const { isDocsAndExtractActive } = useIsDocsAndExtractActive();

  const noDocumentsExtracted = !extractOcrStatus.result?.documents?.some(
    (doc) => doc.status === EXTRACT_OCR_STATUS.EXTRACTED,
  );

  const {
    form,
    formData,
    metadata: savedMetadata,
  } = useGetFormData({
    dischargeLetterForms,
    formName,
    rehabForms,
    transitionalCare,
  });

  const hasPreviousFormValue = !!formData;

  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [formDataState, setFormDataState] = useState<
    DocsFormDataMapped<T> | undefined
  >(undefined);

  const { config, skipRequiredValidation } = useGetFormFieldConfig(
    formName,
    translations,
  );

  const initialValues = config.getInitialValue({
    patient,
    rehabForms,
    value: formData,
  });

  const methods = useForm<DocsFormDataMapped<T>>({
    defaultValues: initialValues,
    disabled: footerScreen !== DOCS_FORM_FOOTER_SCREENS.DEFAULT,
    resolver: zodResolver(
      config.schema as z.ZodType<DocsFormDataMapped<T>, DocsFormDataMapped<T>>,
    ),
  });

  const onError = useCallback(() => {
    setHeaderBannerAction({ banner: HEADER_BANNERS.EXTRACT_FAILED });
  }, []);

  const onPending = useCallback(() => {
    setHeaderBannerAction({ banner: HEADER_BANNERS.EXTRACTING });
  }, []);

  const onSuccess = useCallback(
    (result: DocsFormStatusResponse) => {
      if (!result.form_stats?.filled_fields) {
        setHeaderBannerAction({
          banner: HEADER_BANNERS.EXTRACT_COMPLETED_NONE_FILLED,
        });
        return;
      }

      if (
        hasPreviousFormValue &&
        headerBannerAction.banner !== HEADER_BANNERS.IMPORT_WARNING
      ) {
        // check whether we are going to overwrite something, if so show the overwrite warning
        const overwriteFields = Object.entries(result.form).filter(
          ([field, { value }]) => {
            if (value == null || value === "") return false;
            const currentValue = methods.getValues(
              field as Path<DocsFormDataMapped<T>>,
            );
            return currentValue && currentValue !== value;
          },
        );

        if (overwriteFields.length > 0) {
          setHeaderBannerAction({ banner: HEADER_BANNERS.IMPORT_WARNING });
          return;
        }
      }
      setHeaderBannerAction({
        banner: HEADER_BANNERS.EXTRACT_COMPLETED,
        context: {
          numFilledFields: result.form_stats.filled_fields,
        },
      });
      Object.entries(result.form).forEach(([field, { value }]) => {
        if (value == null || value === "") return;
        methods.setValue(
          field as Path<DocsFormDataMapped<T>>,
          value as PathValue<
            DocsFormDataMapped<T>,
            Path<DocsFormDataMapped<T>>
          >,
          {
            shouldDirty: true,
          },
        );
      });
    },
    [hasPreviousFormValue, headerBannerAction, methods],
  );

  const {
    fillForm: docsFillForm,
    resetResult: resetDocsFillFormResult,
    result: docsFillFormResult,
  } = useDocsFillForm({
    careseekerId: patient.careseeker?.id,
    onError,
    onPending,
    onSuccess,
    patientId: patient.id,
    skip: noDocumentsExtracted,
  });

  const sourceInformation = useSelectedSource<T>({
    docsFillFormResult,
    headerBannerAction,
    savedMetadata,
  });

  const { getIsOverwritingData, importFormData } = useImportFormData({
    auction,
    formGetters: config.auctionToForm(getOntology, translations),
    formOnChange: (value, field) => {
      if (value == null || value === "") return;
      methods.setValue(field as Path<DocsFormDataMapped<T>>, value, {
        shouldDirty: true,
      });
    },
    formValueGetter: (field) => {
      return methods.getValues(field as Path<DocsFormDataMapped<T>>);
    },
  });

  const handleImportData = async () => {
    try {
      setImportFormProgress(QUERY_PROGRESS_PENDING);
      if (
        hasPreviousFormValue &&
        headerBannerAction.banner !== HEADER_BANNERS.IMPORT_WARNING &&
        getIsOverwritingData()
      ) {
        setHeaderBannerAction({ banner: HEADER_BANNERS.IMPORT_WARNING });
      } else {
        // to be determined how this will merge with auction data
        if (isDocsAndExtractActive) {
          await docsFillForm(formName);
          return;
        }
        switch (formName) {
          case DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT:
          case DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY: {
            await docsFillForm(formName);
            break;
          }
          default:
            importFormData();
            setHeaderBannerAction({ banner: null });
            break;
        }
      }
    } catch (error) {
      setImportFormProgress(QUERY_PROGRESS_FAILED);
      console.error("error with import form data", error);
    } finally {
      setImportFormProgress(QUERY_PROGRESS_SUCCEED);
    }
  };

  const { handleOpenPdf, handleSendToHis, progressOpenPdf, progressSendToHis } =
    useCreatePdf({
      auction,
      footerScreen,
      formData: formData as DocsFormDataMapped<T>,
      formName,
      isDirty: methods.formState.isDirty,
      patient,
      rehabForms,
      setFooterBanner,
      setFooterScreen,
      transitionalCare,
    });

  const handleBack = () => {
    setFooterScreen(DOCS_FORM_FOOTER_SCREENS.DEFAULT);
    setFormDataState(undefined);
  };

  const { onSubmit, onSubmitProgress } = useGetOnSubmit({
    auction,
    docsFillFormResult,
    formData,
    formName,
    hasPreviousFormValue,
    metadata: savedMetadata,
    rehabForms,
    sourceInformation,
  });

  const handleSubmit = methods.handleSubmit(
    async (data: DocsFormDataMapped<T>) => {
      switch (footerScreen) {
        case DOCS_FORM_FOOTER_SCREENS.DEFAULT:
          setFooterScreen(DOCS_FORM_FOOTER_SCREENS.SAVE_CONFIRMATION);
          setFormDataState(data);
          break;
        case DOCS_FORM_FOOTER_SCREENS.SAVE_CONFIRMATION: {
          if (!confirmationChecked || !formDataState) return;
          await onSubmit(formDataState, methods.formState.isDirty);
          methods.reset(formDataState);
          handleBack();
          setFooterBanner(
            hasPreviousFormValue
              ? FOOTER_BANNERS.OVERWRITE_SUCCESS
              : FOOTER_BANNERS.SAVE_SUCCESS,
          );
          resetDocsFillFormResult();
          break;
        }
      }
    },
  );

  const { deleteFormProgress: handleDeleteProgress, onDelete } =
    useDeleteConfirmation({
      auction,
      formName: formName as RehabFormName,
    });

  const handleDelete = async () => {
    try {
      await onDelete();
      methods.reset(config.getInitialValue({ patient, rehabForms }));
      setFooterBanner(FOOTER_BANNERS.DELETE_SUCCESS);
      setHeaderBannerAction({ banner: null });
      resetDocsFillFormResult();
      handleBack();
    } catch (error) {
      console.error(error);
    }
  };

  const onCloseHeaderBanner = () => {
    setHeaderBannerAction({ banner: null });
  };

  useEffect(() => {
    setConfirmationChecked(false);
  }, [footerScreen]);

  useEffect(() => {
    if (!isDocsAndExtractActive) return;

    if (noDocumentsExtracted) {
      setHeaderBannerAction({ banner: HEADER_BANNERS.NO_FILES_WARNING });
      return;
    }
    setHeaderBannerAction({ banner: null });
  }, [noDocumentsExtracted, isDocsAndExtractActive]);

  return {
    config,
    confirmationChecked,
    footerBanner,
    footerScreen,
    form,
    formData,
    handleBack,
    handleDelete,
    handleDeleteProgress,
    handleOpenPdf,
    handleSendToHis,
    handleSubmit,
    hasPreviousFormValue,
    headerBannerAction,
    importFormData: handleImportData,
    importFormProgress,
    methods,
    noDocumentsExtracted,
    onCloseHeaderBanner,
    onSubmit,
    onSubmitProgress,
    progressOpenPdf,
    progressSendToHis,
    setConfirmationChecked,
    setFooterBanner,
    setFooterScreen,
    setHeaderBannerAction,
    skipRequiredValidation,
    sourceInformation,
  };
}
